import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useEvents } from "./context/EventsContext";
import useToggleSubscription from "./hooks/usetoggleSubscription";
import useKeyboardNavigation from "./hooks/useKeyboardNavigation";
import useBackNavigation from "./hooks/useBackNavigation";
import useMenuData from "./hooks/useMenuData";
import useEPGState from "./hooks/useEPGState";
import usePendo from "./hooks/usePendo";
import useActiveLiveContentSync from "./hooks/useActiveLiveContentSync";
import useInactivityClose from "./hooks/useInactivityClose";
import EPGContent from "./EPGContent";
import EPGHeader from "./EPGHeader";
import EPGSearchView from "./search/EPGSearchView";
import ConfirmDialog from "../components/ConfirmDialog/ConfirmDialog";
import TermsPopup from "./components/Terms";
import EPGSidePanel from "./EPGSidePanel";
import { CONTENT_TYPE, EVENT_STATUS, FIRST_EVENT_KEY } from "./constants/event";
import { BASE_URL } from "./constants/general";
import { isContentNowPlaying } from "./utils/shared/eventStatus";
import { shouldCloseEPG } from "./utils/shared/eventNavigation";
import { getHeaders } from "./utils/shared/headers";
import { handleExitApp } from "../utils/exitApp";
import { getModel } from "../utils/models";
import { runReadinessLoading, surfaceReadinessResult, surfaceVisualTestUpNext, showTestStreamBanner, dismissTestStreamBanner } from "../states/readiness/bootOverlay";
import { fetchAndCacheState } from "../Upshow";
import "./epg.scss";

const App = ({ deeplinkEventIdUrl, orgShouldCheckToSignTC, showMarketingButton, showExitAppPopUp, shouldApplyComcastStyles }) => {
    const { activeLiveContent, completeLiveEvents, liveEvents, channels, fetchEvents, resetFilter, handleFilterSelect, loading, showFiltered } = useEvents();
    // EPG state management
    const { isOpen, selectedEvent, deeplinkEventId, showTermsPopup, setShowTermsPopup, openEPG, closeEPG, clearSelection, setSelectedEvent, setFocusKeyToReturn, focusKeyToReturn } = useEPGState(resetFilter, deeplinkEventIdUrl, orgShouldCheckToSignTC);
    const { sportsData, leaguesData, partnersData } = useMenuData(completeLiveEvents);
    const { handleToggleSubscription, isSubscribeLoading } = useToggleSubscription(fetchEvents, activeLiveContent);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showEventNotAvailableWarning, setShowEventNotAvailableWarning] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    const [deeplinkEvent, setDeeplinkEvent] = useState(null);
    const showSidePanel = isOpen && selectedEvent && activeLiveContent;

    useEffect(() => {
        if (!deeplinkEventId) {
            setDeeplinkEvent(null);
            return; 
        }

        if (!activeLiveContent || !completeLiveEvents.length) {
            return;
        }

        const deeplinkIsActive = deeplinkEventId === activeLiveContent.id && activeLiveContent.contentType === CONTENT_TYPE.LIVE_EVENT;
        
        if (deeplinkIsActive) {
            return;
        }

        const eventDeeplink = completeLiveEvents.find(({ id }) => id === deeplinkEventId);
        
        if (eventDeeplink && !selectedEvent) {
            const deeplinkEventIsLive = liveEvents.find(({ id }) => id === deeplinkEventId);
            setDeeplinkEvent(eventDeeplink);
            
            if (deeplinkEventIsLive) {
                handlePlayNow(eventDeeplink);
            } else {
                openEPG();
                handleCardSelect({ event: eventDeeplink, focusKey: FIRST_EVENT_KEY });
            }
        } else if (!eventDeeplink) { 
            setShowEventNotAvailableWarning(true);
        }
    }, [liveEvents, completeLiveEvents, activeLiveContent, deeplinkEventId]);

    useEffect(() => {
        if (selectedEvent && selectedEvent.contentType !== CONTENT_TYPE.LIVE_CHANNEL) {
            const updatedEvent = completeLiveEvents.find(({ id }) => id === selectedEvent.id);
            setSelectedEvent(updatedEvent);
        }
    }, [completeLiveEvents]);

    // Back Navigation
    const { goBack } = useBackNavigation({
        states: {
            isOpen,
            showConfirmDialog,
            showTermsPopup,
            selectedEvent,
            showFiltered,
            showSearch,
            showEventNotAvailableWarning
        },
        actions: {
            closeSidePanel: clearSelection,
            closeFiltered: resetFilter,
            closeSearch: () => setShowSearch(false),
            closeTerms: () => {
                clearSelection();
                setShowTermsPopup(false);
                if (orgShouldCheckToSignTC && !selectedEvent) { // Should not close inside epg, only pre-opening
                    closeEPG();
                }
            },
            closeConfirmDialog: () => setShowConfirmDialog(false),
            closeEPG,
            exitApp: () => setShowConfirmDialog(showExitAppPopUp),
            closeEventNotAvailable: () => {
                setShowEventNotAvailableWarning(false);
                openEPG();
            }
        },
    });

    const handleOpenEpg = useCallback(() => {
        if (showConfirmDialog || loading || showEventNotAvailableWarning) return;
        openEPG();
    }, [showConfirmDialog, openEPG, loading, showEventNotAvailableWarning]);

    const handleOpenSearch = useCallback(() => {
        if (loading) return;
        resetFilter();
        setShowSearch(true);
    }, [loading, resetFilter]);

    useEffect(() => {
        if (!isOpen) setShowSearch(false);
    }, [isOpen]);

    // Navigation
    const { pause, resume } = useKeyboardNavigation(handleOpenEpg, goBack, closeEPG, isOpen);

    const closeEPGRef = useRef(closeEPG);
    closeEPGRef.current = closeEPG;
    const prevActiveLiveContentIdRef = useRef(activeLiveContent?.id);
    const prevActiveLiveContentStatusRef = useRef(activeLiveContent?.status);

    // Close the EPG when a live event starts playing while it is already open, so the
    // customer is returned to their live content.
    useEffect(() => {
        if (!isOpen) {
            prevActiveLiveContentIdRef.current = activeLiveContent?.id;
            prevActiveLiveContentStatusRef.current = activeLiveContent?.status;
            return;
        }

        const prevId = prevActiveLiveContentIdRef.current;
        const prevStatus = prevActiveLiveContentStatusRef.current;
        const currentId = activeLiveContent?.id;
        const currentStatus = activeLiveContent?.status;
        prevActiveLiveContentIdRef.current = currentId;
        prevActiveLiveContentStatusRef.current = currentStatus;

        const isLiveEvent = activeLiveContent?.contentType === CONTENT_TYPE.LIVE_EVENT;
        const newEventStarted = currentId && currentId !== prevId && isLiveEvent;
        const sameEventWentLive = currentId && currentId === prevId && currentStatus === EVENT_STATUS.LIVE && prevStatus !== EVENT_STATUS.LIVE && isLiveEvent;
        const liveEventStarted = newEventStarted || sameEventWentLive;

        if (liveEventStarted) {
            closeEPGRef.current();
        }
    }, [activeLiveContent, isOpen]);

    // Close the EPG after 2 minutes of inactivity while a live event plays in the background
    useInactivityClose(isOpen, activeLiveContent, closeEPG);

    // Initialize pendo the first time the EPG opens
    usePendo(isOpen);

    // Sync activeLiveContent on LiveContentUpdate events
    useActiveLiveContentSync();

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchEvents(true);
        }
    }, [isOpen]);

    const handleChannelSelect = (channel) => {
        const filterItem = {
            name: channel.subscriptionType?.display_name || channel.title,
            searchType: "subscription_type",
            searchTerm: channel.subscriptionType?.backwards_compatible_name,
            channelData: {
                bannerUrl: channel.subscriptionType?.banner_url,
                channelName: channel.title,
                channelId: channel.id,
            },
        };
        handleFilterSelect(filterItem, { type: "partner" });
    };

    const [isSubscribedToTestStream, setIsSubscribedToTestStream] = useState(
        () => window.__UpshowState?.isSubscribedToTestStream ?? false
    );
    const [isTestStreamLoading, setIsTestStreamLoading] = useState(false);

    useEffect(() => {
        const handleStateUpdate = (event) => {
            setIsSubscribedToTestStream(event.detail?.isSubscribedToTestStream ?? false);
        };
        window.addEventListener("UpshowStateUpdate", handleStateUpdate);
        return () => window.removeEventListener("UpshowStateUpdate", handleStateUpdate);
    }, []);

    const handleTestStream = useCallback(async () => {
        if (isTestStreamLoading) return;
        setIsTestStreamLoading(true);

        if (isSubscribedToTestStream) {
            setIsSubscribedToTestStream(false);
            if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = false;
            try {
                await axios.post(`${BASE_URL}/v6/unsubscribe_test_stream`, {}, { headers: getHeaders() });
                dismissTestStreamBanner();
                fetchAndCacheState();
                closeEPG();
            } catch (err) {
                console.error("Test stream unsubscribe error:", err);
                setIsSubscribedToTestStream(true);
                if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = true;
            } finally {
                setIsTestStreamLoading(false);
            }
            return;
        }

        closeEPG();
        pause();
        try {
            const result = await runReadinessLoading();
            await surfaceReadinessResult(result, { dismissLabel: 'Continue' });
            const shouldRun = await surfaceVisualTestUpNext();
            if (shouldRun) {
                await axios.post(`${BASE_URL}/v6/subscribe_test_stream`, {}, { headers: getHeaders() });
                setIsSubscribedToTestStream(true);
                if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = true;
                showTestStreamBanner();
                fetchAndCacheState();
            }
        } catch (err) {
            console.error('Test stream subscribe error:', err.message);
        } finally {
            resume();
            setIsTestStreamLoading(false);
        }
    }, [isTestStreamLoading, isSubscribedToTestStream, closeEPG, pause, resume]);

    const model = getModel();
    const showReadinessBanner = model.includes("chromeos") || model === "brightsign";

    const handleWatchNowChannel = async (channelData) => {
        const channel = channels.find((ch) => ch.id === channelData.channelId);
        if (channel) {
            await handleToggleSubscription(channel);
            resetFilter();
            clearSelection();
            closeEPG();
        }
    };

    const handleCardSelect = ({ event, focusKey }) => {
        if (event.contentType === CONTENT_TYPE.LIVE_CHANNEL) {
            handleChannelSelect(event);
            return;
        }

        setSelectedEvent(event);
        setFocusKeyToReturn(focusKey);

        const isActiveContent = activeLiveContent && activeLiveContent.id === event.id;

        if (event.shouldSignTC) {
            setShowTermsPopup(true);
        } else if (isActiveContent && activeLiveContent.contentType === CONTENT_TYPE.LIVE_EVENT) {
            closeEPG();
        }
    };

    const handlePlayNow = async (event) => {
        await handleToggleSubscription(event);
        clearSelection();

        if (shouldCloseEPG(event, activeLiveContent, focusKeyToReturn)) {
            closeEPG();
        }
    };

    // Manage app root class
    useEffect(() => {
        const appRoot = document.getElementById('app-root');
        if (!appRoot) return;

        if (isOpen) {
            appRoot.classList.add('epg-active');
        } else {
            appRoot.classList.remove('epg-active');
        }

        return () => {
            appRoot.classList.remove('epg-active');
        };
    }, [isOpen]);

    return (
        <>
            {isOpen && (
                <div className="epgContainer">
                    <EPGHeader goBack={goBack} closeEPG={closeEPG} showMarketingButton={showMarketingButton} onOpenSearch={handleOpenSearch} showSearch={showSearch} />
                    {showSearch ? (
                        <EPGSearchView
                            onEnterPress={handleCardSelect}
                            shouldApplyComcastStyles={shouldApplyComcastStyles}
                        />
                    ) : (
                        <EPGContent
                            deeplinkEvent={deeplinkEvent}
                            shouldApplyComcastStyles={shouldApplyComcastStyles}
                            sportsData={sportsData}
                            leaguesData={leaguesData}
                            partnersData={partnersData}
                            onEnterPress={handleCardSelect}
                            onWatchNow={handleWatchNowChannel}
                            onRunReadinessTest={showReadinessBanner ? handleTestStream : undefined}
                            readinessIsSubscribed={isSubscribedToTestStream}
                            readinessIsLoading={isTestStreamLoading}
                        />
                    )}
                </div>
            )}
            {showSidePanel && <EPGSidePanel event={selectedEvent} isLoading={isSubscribeLoading || loading} isNowPlaying={isContentNowPlaying(selectedEvent, activeLiveContent)} onClose={goBack} onPlay={handlePlayNow} showBackdrop={showSearch}/>}
            {showTermsPopup && <TermsPopup event={selectedEvent} onClose={goBack} orgShouldCheckToSignTC={orgShouldCheckToSignTC}/>}
            {showConfirmDialog && (
                <ConfirmDialog
                    title="Leaving EverPass?"
                    description="You’re heading to the home screen. Stay here to keep watching."
                    confirmTitle="Go to home screen"
                    cancelTitle="Stay on EverPass"
                    onConfirmPressed={() => {
                        setShowConfirmDialog(false);
                        handleExitApp();
                    }}
                    onCancelPressed={() => goBack()}
                />
            )}
            {showEventNotAvailableWarning && (
                <ConfirmDialog
                    title="Event Unavailable"
                    description="This event can’t be played due to regional or subscription restrictions."
                    confirmTitle="Go to Xumo"
                    cancelTitle="Open EverPass"
                    onConfirmPressed={() => {
                        setShowEventNotAvailableWarning(false);
                        handleExitApp();
                    }}
                    onCancelPressed={() => {
                        setShowEventNotAvailableWarning(false); 
                        openEPG();
                    }}
                />
            )}
        </>
    );
};
export default App;
